import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';
import { Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import ShippingProvider from '@/shared/types/shipping/shippingProvider.type';

import useSetupOptions from './hooks/useSetupOptions';

type SetupOptionsProps = {
  setupOptionsState: ReturnType<typeof useSetupOptions>;
  shippingProviders: ShippingProvider[];
};

const SetupOptions: FC<SetupOptionsProps> = ({
  setupOptionsState,
  shippingProviders,
}) => {
  const { t } = useTranslation(['shipping', 'forms']);

  const {
    setupOptionsForm,
    setupOptionalStepsForm,
    formErrors,
    setSetupOptionsForm,
    setSetupOptionalStepsForm,
  } = setupOptionsState;

  const getShippingProviderKey = (providerId: string) => {
    const providerById = shippingProviders.find(
      (item) => item.shipping_provider_id === providerId,
    );

    return providerById?.shipping_provider_key || '';
  };

  return (
    <Stack py={2} spacing={2}>
      <WaveUIForm_V2
        fields={[
          {
            field_type: FieldType.Select,
            field_props: {
              required: true,
              name: 'shipping_provider_id',
              label: t('forms:shipping_provider_id'),
              placeholder: t('forms:shipping_provider_id'),
              value: setupOptionsForm.shipping_provider_id,
              options: shippingProviders.map(
                ({ shipping_provider_id, name }) => ({
                  value: shipping_provider_id,
                  label: name,
                }),
              ),
              renderOption: (option) => {
                const providerById = shippingProviders.find(
                  (item) => item.shipping_provider_id === option.value,
                );

                return (
                  <Stack spacing={0.5} direction='row' alignItems='center'>
                    <Typography variant='body1'>
                      {providerById?.name}:
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {providerById?.shipping_provider_key}
                    </Typography>
                  </Stack>
                );
              },
              error: !!formErrors?.shipping_provider_id,
              helperText: formErrors?.shipping_provider_id,
            },
          },
        ]}
        getFieldsValues={(value) => {
          if ('shipping_provider_id' in value) {
            const id = value.shipping_provider_id as string;

            return setSetupOptionsForm((prev) => ({
              ...prev,
              shipping_provider_id: id,
              shipping_provider_key: getShippingProviderKey(id),
            }));
          }

          setSetupOptionsForm((prev) => ({ ...prev, ...value }));
        }}
      />

      <Stack>
        <Typography variant='body1'>{t('shipping:optionalSteps')}</Typography>

        <WaveUIForm_V2
          fields={[
            {
              field_type: FieldType.Switch,
              field_props: {
                name: 'ignore_customs_details',
                label: t('forms:ignore_customs_details'),
                checked: setupOptionalStepsForm.ignore_customs_details,
              },
            },
          ]}
          getFieldsValues={(value) => {
            setSetupOptionalStepsForm((prev) => ({ ...prev, ...value }));
          }}
        />
      </Stack>
    </Stack>
  );
};

export default SetupOptions;
